import React, { useEffect, useState } from "react";
import { FileDownloadCommentView } from "./fields/file-view-field";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { CommentFileUploadField } from "./fields/file-upload-field";
import axios from "axios";
import { url } from "../../config";
import successToast from "../toast/successToast";
import errorToast from "../toast/errorToast";
import ImageViewModal from "../modal/image-view-modal";
import {
  Paperclip,
  Trash2,
  FileText,
  Film,
  Eye,
  ExternalLink,
  Download,
  X,
} from "lucide-react";

export const FileCommentComponent = ({ tableName, viewId }) => {
  const [fileData, setFileData] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);

  const initialValues = {
    comment: "",
  };

  const validationSchema = Yup.object().shape({});

  // Resolve valid URL string
  const getUrlString = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null)
      return item.url || item.path || "";
    return "";
  };

  // Resolve display file name
  const getFileName = (item) => {
    if (typeof item === "object" && item !== null && item.name) {
      return item.name;
    }

    const urlStr = getUrlString(item);
    if (!urlStr) return "Download File";

    return urlStr.substring(urlStr.lastIndexOf("/") + 1) || "Download File";
  };

  // Resolve file extension category (image, video, document)
  const getFileType = (fileName, fileUrl) => {
    const target = (fileName || fileUrl || "").toLowerCase();
    if (
      /\.(apng|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|png|svg|webp|bmp|ico)$/i.test(
        target,
      )
    ) {
      return "image";
    }
    if (/\.(mp4|webm|ogg|mov|m4v|mkv|avi)$/i.test(target)) {
      return "video";
    }
    const extMatch = target.match(/\.([a-z0-9]+)(\?.*)?$/i);
    return extMatch ? extMatch[1] : "file";
  };

  // Format file size if provided
  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return null;
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const fileCommentsList = () => {
    const token = localStorage.getItem("accessToken");

    axios
      .post(
        `${url}/ffadmin/list-file-comment`,
        {
          model_name: tableName,
          record_id: viewId,
        },
        {
          headers: {
            "x-access-token": token,
          },
        },
      )
      .then((res) => {
        setFileData(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("API Error:", err);
      });
  };

  const deleteFileComment = (id) => {
    const token = localStorage.getItem("accessToken");

    axios
      .post(
        `${url}/ffadmin/delete-file-comment`,
        { id },
        {
          headers: {
            "x-access-token": token,
          },
        },
      )
      .then((res) => {
        successToast(res.message);
        fileCommentsList();
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message || "Delete failed");
      });
  };

  useEffect(() => {
    fileCommentsList();
  }, [tableName, viewId]);

  return (
    <div className="w-full">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-indigo-50 text-primary rounded-lg">
            <Paperclip className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 tracking-tight">
              File Attachments
            </h3>
            <p className="text-xs text-slate-400">
              {fileData?.length
                ? `${fileData.length} files attached`
                : "Shared files and documents"}
            </p>
          </div>
        </div>
      </div>

      {/* Attachment List */}
      <div className="space-y-3 mb-6">
        {fileData && fileData.length > 0 ? (
          fileData.map((item, index) => {
            const rawUrl = item?.url || item;
            const fileUrl = getUrlString(rawUrl);
            const fileName = getFileName(rawUrl);
            const fileSize = typeof rawUrl === "object" ? rawUrl.size : null;
            const fileCategory = getFileType(fileName, fileUrl);

            return (
              <div
                key={item?.id || index}
                className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition hover:shadow-md flex items-center justify-between gap-3 w-full"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {/* Preview Thumbnail / Icon */}
                  <div className="relative flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                    {fileCategory === "image" ? (
                      <img
                        src={fileUrl}
                        alt={fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : fileCategory === "video" ? (
                      <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                        <video
                          src={fileUrl}
                          className="w-full h-full object-cover opacity-60"
                        />
                        <Film className="absolute w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center">
                        <FileText className="w-4 h-4 text-indigo-600 mb-0.5" />
                        <span className="text-[8px] font-bold uppercase text-gray-500 max-w-[32px] truncate">
                          {fileCategory}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* File Name & Details */}
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-xs font-semibold text-gray-800 truncate"
                      title={fileName}
                    >
                      {fileName}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {formatFileSize(fileSize) || "Attached file"}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  {(fileCategory === "image" || fileCategory === "video") && (
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewMedia({
                          url: fileUrl,
                          type: fileCategory,
                          name: fileName,
                        })
                      }
                      className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}

                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>

                  <a
                    href={fileUrl}
                    download={fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                    title="Download File"
                  >
                    <Download className="w-4 h-4" />
                  </a>

                  <button
                    type="button"
                    onClick={() => deleteFileComment(item?.id)}
                    className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition ml-1"
                    title="Delete file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-6 px-4 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-xs text-slate-400 font-medium">
              No attachments uploaded yet.
            </p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 z-[5001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>
            {previewMedia.type === "video" ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg"
              />
            ) : (
              <img
                src={previewMedia.url}
                alt={previewMedia.name || "Preview"}
                className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
              />
            )}
          </div>
        </div>
      )}

      {/* Upload Field Section */}
      <div className="pt-2">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={() => {}}
          enableReinitialize={true}
        >
          <Form>
            <CommentFileUploadField
              label={"comment"}
              name={"comment"}
              placeholder={`Enter comment`}
              tableName={tableName}
              viewId={viewId}
              setFileData={setFileData}
              fileData={fileData}
            />
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export default FileCommentComponent;
